import React, { useEffect, useRef, useState } from 'react';
import Message from './Message';
import Loading from '../common/Loading';

const MessageList = ({ messages = [], loading }) => {
  const messagesEndRef = useRef(null);
  const [expandedRounds, setExpandedRounds] = useState({});

  // Group messages by conversation_round_num
  const groupedMessages = messages.reduce((groups, message) => {
    const roundNum = message.conversation_round_num || 0;
    if (!groups[roundNum]) {
      groups[roundNum] = [];
    }
    groups[roundNum].push(message);
    return groups;
  }, {});

  // Initialize expandedRounds state - all collapsed by default
  useEffect(() => {
    if (messages.length > 0) {
      // Get all unique round numbers
      const roundNums = [...new Set(messages.map(m => m.conversation_round_num || 0))];
      
      // Initialize - all rounds collapsed by default
      const initialExpandState = roundNums.reduce((acc, round) => {
        acc[round] = false; // Set all to collapsed (false)
        return acc;
      }, {});
      
      setExpandedRounds(initialExpandState);
    }
  }, [messages]);

  const toggleRoundExpansion = (roundNum) => {
    setExpandedRounds(prev => ({
      ...prev,
      [roundNum]: !prev[roundNum]
    }));
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, expandedRounds]);

  if (!messages || messages.length === 0) {
    return (
      <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
        <div className="flex flex-col items-center justify-center h-full text-center">
          <div className="w-16 h-16 mb-4 text-gray-400">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900">No messages yet</h3>
          <p className="mt-1 text-gray-500">
            Start a conversation by sending a message below
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
      {Object.entries(groupedMessages).map(([roundNum, roundMessages]) => {
        const isExpanded = expandedRounds[roundNum];
        const roundNumInt = parseInt(roundNum);
        const hasMultipleMessages = roundMessages.length > 1;
        const firstMessage = roundMessages[0];
        
        // If there's only one message in this round, just render it normally
        if (!hasMultipleMessages) {
          return (
            <div key={`round-${roundNum}`} className="mb-4">
              <Message message={firstMessage} />
            </div>
          );
        }
        
        // For rounds with multiple messages - show the last message by default
        const lastMessage = roundMessages[roundMessages.length - 1];
        return (
          <div key={`round-${roundNum}`} className="mb-6">
            {/* Always render the last message */}
            <Message key={lastMessage.id} message={lastMessage} />
            
            {/* Only show expand/collapse controls for rounds with multiple messages */}
            <div className="flex justify-end items-center my-2 pr-4">
              <button 
                onClick={() => toggleRoundExpansion(roundNumInt)}
                className="flex items-center text-sm text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded-md"
              >
                <span className="mr-2">
                  {isExpanded ? 'Hide' : 'Show'} {roundMessages.length - 1} more message{roundMessages.length - 1 !== 1 ? 's' : ''}
                </span>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  className={`transition-transform ${isExpanded ? 'transform rotate-180' : ''}`}
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
            </div>
            
            {/* Collapsible section for additional messages */}
            <div className={`transition-all duration-300 overflow-hidden ${isExpanded ? 'max-h-full opacity-100' : 'max-h-0 opacity-0'}`}>
              {roundMessages.slice(0, -1).map((message) => (
                <Message key={message.id} message={message} />
              ))}
            </div>
          </div>
        );
      })}
      
      {loading && (
        <div className="p-4 text-center">
          <Loading size="small" text="Assistant is thinking..." />
        </div>
      )}
      <div ref={messagesEndRef} />
    </div>
  );
};

export default MessageList;